import { Paperclip } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from '@/shared/i18n/use-translation';
import { Button } from '@/shared/ui/button/button';
import type { Attachment } from './attachment';
import { AttachmentItem } from './attachment-item';
import { useAddFiles } from './use-add-files';

type AttachmentsFieldProps = {
  readonly value: readonly Attachment[];
  readonly onChange: (value: Attachment[]) => void;
  readonly label?: string;
};

export function AttachmentsField({ value, onChange, label }: AttachmentsFieldProps) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const { busy, add } = useAddFiles(function append(added) {
    const known = new Set(value.map((a) => a.sha256));
    onChange([...value, ...added.filter((a) => !known.has(a.sha256))]);
    if (input.current) input.current.value = '';
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label ?? t('Files')}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          <Paperclip /> {t('Add')}
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          accept="image/*,application/pdf,.doc,.docx,.odt,.txt"
          className="hidden"
          onChange={(event) => void add(event.target.files)}
        />
      </div>
      {value.length > 0 ? (
        <ul className="flex flex-col divide-y rounded-lg border">
          {value.map((attachment) => (
            <AttachmentItem
              key={attachment.sha256}
              attachment={attachment}
              onRemove={() => onChange(value.filter((a) => a.sha256 !== attachment.sha256))}
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('Photos, PDFs or scans. They are kept on all your devices so you can see them offline.')}
        </p>
      )}
    </div>
  );
}
